import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '@guryou/core';
import { Block, CheckBox, GoogleMap, GoogleMapsAutocomplete, HiddenField, TextAreaBox, TextBox } from '@guryou/html-components';
import { sizes } from '@guryou/html-theme';
import { parseGoogleMapLocation, parseGoogleMapPlace } from '@guryou/utils';
import { useOnboardContext } from 'onboard/context';
import SelectableLocationGroup from './LocationCube';

const hasAddress = values => {
  return values?.address && values?.latitude && values?.longitude;
};

const LocationPicker = ({ showCheckBox, checkBoxLabel, location, disabled = false }) => {
  const { t } = useTranslation();
  const mapsAutocomplete = useRef();
  const defaultValue = location?.address
    ? LocationType.mapLocation
    : location?.note || location?.url
    ? LocationType.plainNote
    : LocationType.providerLocation;

  const { location: providerLocation } = useOnboardContext();
  const [locationObject, setLocationObject] = useState(location || {});
  const [selectedView, setSelectedView] = useState(defaultValue);

  // const providerDetails = useSelector(state => state.provider.generalInfo);

  const handleAutocompleteChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
  };

  const handleAutocompleteOnChange = e => {
    if (!e?.target?.value) {
      setLocationObject({});
    }
    const val = e?.target?.value;
    setLocationObject(prev => ({ ...prev, address: val }));
  };

  return (
    <Block margin={0}>
      <SelectableLocationGroup onChange={val => setSelectedView(val)} value={defaultValue} />

      {selectedView === LocationType.plainNote && (
        <>
          <TextBox
            disabled={disabled}
            xs={12}
            fullWidth
            margin="10px 0px 20px 0px"
            label="URL"
            name="url"
            value={locationObject.url || locationObject.locationNote || locationObject.note || ''}
          />
          <TextAreaBox
            xs={12}
            disabled={disabled}
            fullWidth
            margin="10px 0px 20px 0px"
            label={t('GENERAL.leaveNote')}
            placeholder={t('GENERAL.enterValidUrl')}
            name="locationNote"
            value={locationObject.locationNote || locationObject.note || ''}
          />
        </>
      )}
      {selectedView === LocationType.mapLocation && (
        <>
          <HiddenField name="latitude" value={locationObject.latitude || ''} />
          <HiddenField name="longitude" value={locationObject.longitude || ''} />
          <HiddenField name="address" value={locationObject.address || ''} />
          <GoogleMapsAutocomplete
            disabled={disabled}
            fullWidth
            margin="none"
            label={t('GENERAL.location')}
            inputRef={mapsAutocomplete}
            value={locationObject?.address || ''}
            onBlur={handleAutocompleteChange}
            onChange={handleAutocompleteOnChange}
            placeholder={t('GENERAL.enterLocation')}
            required
          />
          <Block margin={0} marginTop={4}>
            <GoogleMap
              style={{ height: '25vh', minHeight: `${sizes.small}px`, width: `100%` }}
              defaultCenter={{ lat: locationObject.latitude ?? 41.9028, lng: locationObject.longitude ?? 12.4964 }}
              autocompleteRef={mapsAutocomplete}
              isMarkerShown={!!locationObject.latitude}
              onMapClick={location => setLocationObject(parseGoogleMapLocation(location))}
              onPlaceSelected={place => setLocationObject(parseGoogleMapPlace(place))}
            />
          </Block>
        </>
      )}

      {selectedView === LocationType.providerLocation && hasAddress(providerLocation) && (
        <>
          <GoogleMapsAutocomplete
            disabled={true}
            fullWidth
            margin="none"
            label={t('GENERAL.location')}
            inputRef={mapsAutocomplete}
            value={providerLocation?.address || ''}
            placeholder={t('GENERAL.enterLocation')}
            required
          />
          <Block margin={0} marginTop={4}>
            <GoogleMap
              style={{ height: '25vh', minHeight: `${sizes.small}px`, width: `100%` }}
              defaultCenter={{ lat: providerLocation.latitude ?? 41.9028, lng: providerLocation.longitude ?? 12.4964 }}
              autocompleteRef={mapsAutocomplete}
              isMarkerShown={!!providerLocation.latitude}
              disabled={true}
            />
          </Block>
        </>
      )}
      {showCheckBox && <CheckBox fullWidth={true} label={checkBoxLabel} name="propagateLocation" value={true} />}
    </Block>
  );
};

export default LocationPicker;
